import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from "react";
import { useAuth } from "./AuthContext";
import { clearPresignCache, resolveReferencePhotoUrl } from "../utils/presignedImageUrl";
import { clearAdminWorkSessionStorage, persistAdminWorkSessionId } from "../utils/adminWorkSessionStorage";
import { useWizardStore } from "../store/wizardStore";
import type { StoryStyleId } from "../constants/storyStyles";
import { normalizeFrontendStoryStyleId } from "../constants/storyStyles";
import { API_BASE_URL, API_ORIGIN_BASE_URL } from "../config/apiBaseUrl";

/** Legacy default from API before empty descriptions were allowed; treat as no user input. */
const LEGACY_STORY_DESCRIPTION_PLACEHOLDER =
  "Draft story - description will be updated later";

function normalizeLoadedStoryDescription(value: string | undefined | null): string {
  const raw = value ?? "";
  const trimmed = raw.trim();
  if (!trimmed || trimmed === LEGACY_STORY_DESCRIPTION_PLACEHOLDER) return "";
  return raw;
}

// Types
type ImageData = {
  file: File;
  preview: string;
  tags: string;
  uploaded?: boolean;
  serverId?: string;
  /** R2 object key when stored in private bucket (preview may be filled after presign). */
  storageKey?: string;
  /** When set by the user for accessibility, used as img alt; otherwise alt stays empty. */
  accessibleDescription?: string;
};

type WizardData = {
  // Session management
  sessionId: string | null;
  setSessionId: (id: string) => void;
  /** Set when an admin loads a story via API; submitter email (R2 namespace owner). */
  submissionOwnerEmail: string | null;
  
  // Step 1: Language
  language: string | null;
  setLanguage: (lang: string) => void;
  
  // Step 1b: Story Type + illustration style
  storyType: 'surrogacy' | 'general' | null;
  setStoryType: (type: 'surrogacy' | 'general') => void;
  storyStyle: StoryStyleId;
  setStoryStyle: (style: StoryStyleId) => void;
  updateStoryType: (storyType: 'surrogacy' | 'general', storyStyle: StoryStyleId) => Promise<boolean>;
  
  // Step 2: Images  
  images: ImageData[];
  addImage: (image: ImageData) => void;
  removeImage: (index: number) => void;
  updateImageTags: (index: number, tags: string) => void;
  uploadImages: () => Promise<boolean>;
  persistImageTags: () => Promise<boolean>;
  
  // Step 3: Story
  storyDescription: string;
  setStoryDescription: (description: string) => void;
  storyGuidelines: string;
  setStoryGuidelines: (guidelines: string) => void;
  storyTitle: string;
  setStoryTitle: (title: string) => void;
  submitStoryDescription: () => Promise<boolean>;
  
  // API functions
  createStorySession: () => Promise<boolean>;
  generateStory: () => Promise<any>;
  loadSession: (sessionId: string) => Promise<{ success: boolean; redirectPath?: string }>;
  
  // State management
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  resetWizard: () => void;
};

const WizardContext = createContext<WizardData | undefined>(undefined);

export const WizardProvider = ({ children }: { children: ReactNode }) => {
  const { token, user } = useAuth();
  const prevAuthTokenRef = useRef<string | null>(null);
  const prevAuthUserIdRef = useRef<string | null>(null);

  // State
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [submissionOwnerEmail, setSubmissionOwnerEmail] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [storyType, setStoryTypeState] = useState<'surrogacy' | 'general' | null>(null);
  const [storyStyle, setStoryStyleState] = useState<StoryStyleId>("watercolor");
  const [images, setImages] = useState<ImageData[]>([]);
  const [storyDescription, setStoryDescription] = useState<string>("");
  const [storyGuidelines, setStoryGuidelines] = useState<string>("");
  const [storyTitle, setStoryTitle] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const setStoryType = useCallback((type: 'surrogacy' | 'general') => {
    setStoryTypeState(type);
  }, []);

  // Helper function for API calls
  const apiCall = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
        ...options,
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'API call failed');
      }
      
      return data;
    } catch (err: any) {
      throw new Error(err.message || 'Network error');
    }
  }, [token]);

  // API Functions with useCallback to prevent infinite loops
  const createStorySession = useCallback(async (): Promise<boolean> => {
    if (!language) {
      setError('Please select a language first');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // If we already have a session, update the language instead of creating new
      if (sessionId) {
        const data = await apiCall(`/stories/${sessionId}/language`, {
          method: 'PUT',
          body: JSON.stringify({ language }),
        });
        console.log('✅ Language updated for existing session:', sessionId);
        return true;
      } else {
        // Create new session
        const data = await apiCall('/stories/create', {
          method: 'POST',
          body: JSON.stringify({ language }),
        });

        setSessionId(data.data.sessionId);
        setSubmissionOwnerEmail(null);
        console.log('✅ New story session created:', data.data.sessionId);
        return true;
      }
    } catch (err: any) {
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [language, sessionId, apiCall]);

  const uploadImages = useCallback(async (): Promise<boolean> => {
    if (!sessionId) {
      setError('No session ID. Please start over.');
      return false;
    }

    if (images.length === 0) {
      setError('Please add at least one image');
      return false;
    }

    // IMPORTANT: When resuming, images are restored with `uploaded: true`.
    // We must not upload again; otherwise the backend will append duplicates.
    const imagesToUpload = images.filter((img) => !img.uploaded);
    if (imagesToUpload.length === 0) {
      return true;
    }

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      // Add only images that were not uploaded yet.
      imagesToUpload.forEach((image) => {
        formData.append('images', image.file);
        formData.append('tags', image.tags || '');
      });

      const response = await fetch(`${API_BASE_URL}/stories/${sessionId}/images`, {
        method: 'POST',
        body: formData,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      // Mark only the ones we uploaded.
      setImages((prev) =>
        prev.map((img) => (img.uploaded ? img : { ...img, uploaded: true }))
      );
      console.log('✅ Images uploaded successfully');
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, images, token]);

  const persistImageTags = useCallback(async (): Promise<boolean> => {
    if (!sessionId) {
      setError('No session ID. Please start over.');
      return false;
    }
    if (!token) {
      setError('You must be logged in to save.');
      return false;
    }

    // Only persist tags if there are images on the story already.
    // (If images aren't uploaded yet, uploadImages() must happen first.)
    const anyUploaded = images.some((img) => img.uploaded);
    if (!anyUploaded) return true;

    setIsLoading(true);
    setError(null);
    try {
      const tags = images.map((img) => img.tags || '');
      await apiCall(`/stories/${sessionId}/image-tags`, {
        method: 'PUT',
        body: JSON.stringify({ tags }),
      });
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to save image tags');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, token, images, apiCall]);

  const updateStoryType = useCallback(async (type: 'surrogacy' | 'general', style: StoryStyleId): Promise<boolean> => {
    if (!sessionId) {
      setError('No session ID. Please start over.');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      await apiCall(`/stories/${sessionId}/story-type`, {
        method: 'PUT',
        body: JSON.stringify({ storyType: type, storyStyle: style }),
      });
      setStoryTypeState(type);
      setStoryStyleState(style);
      console.log('✅ Story type updated');
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, apiCall]);

  const submitStoryDescription = useCallback(async (): Promise<boolean> => {
    if (!sessionId) {
      setError('No session ID. Please start over.');
      return false;
    }

    if (!storyDescription.trim()) {
      setError('Please enter a story description');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      await apiCall(`/stories/${sessionId}/description`, {
        method: 'PUT',
        body: JSON.stringify({
          storyDescription,
          storyGuidelines: storyGuidelines || '',
          storyTitle: storyTitle.trim().slice(0, 120),
        }),
      });

      console.log('✅ Story description updated');
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, storyDescription, storyGuidelines, storyTitle, apiCall]);

  const generateStory = useCallback(async (): Promise<any> => {
    if (!sessionId) {
      setError('No session ID. Please start over.');
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await apiCall(`/stories/${sessionId}/generate`, {
        method: 'POST',
      });

      console.log('✅ Story data prepared');
      return data.data;
    } catch (err: any) {
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, apiCall]);

  const loadSession = useCallback(async (sessionIdToLoad: string): Promise<{ success: boolean; redirectPath?: string }> => {
    setIsLoading(true);
    setError(null);
    setSubmissionOwnerEmail(null);

    try {
      console.log('🔄 Loading session:', sessionIdToLoad);
      console.log('  Format check - UUID:', /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sessionIdToLoad));
      console.log('  Format check - MongoDB ObjectId:', /^[0-9a-fA-F]{24}$/.test(sessionIdToLoad));
      
      const data = await apiCall(`/stories/${sessionIdToLoad}`);
      const story = data.data;
      
      console.log('✅ Session loaded:', {
        sessionId: story.sessionId,
        _id: story._id,
        language: story.language,
        hasStory: !!story.generatedStory,
        hasImages: story.images?.length > 0,
        hasPageImages: story.pageImages?.length > 0
      });

      if (!story) {
        throw new Error('Story session not found');
      }

      // Restore session state
      setSessionId(story.sessionId);
      setLanguage(story.language);
      setStoryTypeState(story.storyType || null);
      setStoryStyleState(normalizeFrontendStoryStyleId(story.storyStyle));
      const storyDescriptionLoaded = normalizeLoadedStoryDescription(story.storyDescription);
      setStoryDescription(storyDescriptionLoaded);
      setStoryGuidelines(story.storyGuidelines || '');
      setStoryTitle(typeof story.storyTitle === 'string' ? story.storyTitle : '');

      const ownerFromApi =
        typeof (story as { ownerEmail?: unknown }).ownerEmail === "string"
          ? String((story as { ownerEmail: string }).ownerEmail).trim().toLowerCase()
          : "";
      setSubmissionOwnerEmail(ownerFromApi || null);

      if (user?.isAdmin === true) {
        const adminEmail = user.email?.trim().toLowerCase() ?? "";
        if (adminEmail && ownerFromApi && ownerFromApi !== adminEmail) {
          persistAdminWorkSessionId(story.sessionId);
        }
      }

      // Restore images if available
      if (story.images && story.images.length > 0) {
        // Note: We can't fully restore File objects, but we can restore metadata
        // The images are already uploaded to the server
        // Build an absolute backend base URL for uploaded files.
        const baseUrl = API_ORIGIN_BASE_URL;
        const deriveFilename = (img: any): string => {
          if (img?.filename) {
            const normalizedName = String(img.filename).replace(/\\/g, '/');
            const markerIdx = normalizedName.toLowerCase().lastIndexOf('/uploads/');
            if (markerIdx >= 0) return normalizedName.slice(markerIdx + '/uploads/'.length);
            return normalizedName.split('/').pop() || '';
          }
          if (!img?.path) return '';

          // Extract filename from an absolute/relative path ending in ".../uploads/<filename>"
          const normalized = String(img.path).replace(/\\/g, '/');
          const marker = '/uploads/';
          const idx = normalized.toLowerCase().lastIndexOf(marker);
          if (idx >= 0) return normalized.slice(idx + marker.length);
          return normalized.split('/').pop() || '';
        };

        const previewFromServerImage = (img: any): string => {
          const pu = img?.publicUrl && String(img.publicUrl).trim();
          if (pu && /^https?:\/\//i.test(pu)) return pu;
          // R2-only rows have no local file under /uploads; presign fills preview below.
          if (img?.storageKey) return '';
          const filename = deriveFilename(img);
          return filename ? `${baseUrl}/uploads/${filename}` : '';
        };

        const restored: ImageData[] = [];
        for (const img of story.images) {
          const filename = deriveFilename(img);
          let preview = previewFromServerImage(img);
          if (img.storageKey) {
            const presigned = await resolveReferencePhotoUrl(img, token);
            if (presigned) preview = presigned;
          }
          restored.push({
            file: new File([], img.originalName || "image.jpg", { type: img.mimetype || "image/webp" }),
            preview,
            tags: img?.tags || "",
            uploaded: true,
            serverId: filename || img?.filename,
            storageKey: img.storageKey || undefined,
          });
        }
        setImages(restored);
      } else {
        setImages([]);
      }

      // Determine where to redirect based on progress
      let redirectPath = '/create/language';

      // Book request submitted (production regular user flow) - redirect to summary
      if (story.submittedAt || story.status === 'request_submitted') {
        return { success: true, redirectPath: '/create/order-summary' };
      }

      if (story.generatedStory) {
        // Story is generated, check if images are being worked on
        if (story.pageImages && story.pageImages.length > 0) {
          // Parse story to get page count - each page has 2 slots (main + side picture)
          const parseStoryIntoPages = (storyText: string, lang: string | null): string[] => {
            if (!storyText || storyText.trim().length === 0) return [];
            const isHebrew = lang === 'Hebrew';
            const pagePattern = isHebrew ? /עמוד\s+\d+\s*:/g : /Page\s+\d+\s*:/gi;
            const parts = storyText.split(pagePattern);
            const pages = parts.slice(1).map((p: string) => p.trim()).filter((p: string) => p.length > 0);
            return pages.length > 0 ? pages : [storyText.trim()];
          };
          const pages = parseStoryIntoPages(story.generatedStory, story.language);
          const totalSlotsNeeded = pages.length * 2;
          const hasApprovedForSlot = (pageNumber: number, isSidePicture: boolean) =>
            story.pageImages.some((img: any) => {
              const pn = typeof img.pageNumber === 'number' ? img.pageNumber : parseInt(img.pageNumber);
              const isSide = img.isSidePicture === true;
              return pn === pageNumber && isSide === isSidePicture && img.approved;
            });
          let allSlotsApproved = totalSlotsNeeded > 0;
          for (let i = 0; i < totalSlotsNeeded; i++) {
            const pageNumber = Math.floor(i / 2) + 1;
            const isSidePicture = i % 2 === 1;
            if (!hasApprovedForSlot(pageNumber, isSidePicture)) {
              allSlotsApproved = false;
              break;
            }
          }
          const allPagesApproved = allSlotsApproved;
          if (allPagesApproved && story.coverImage && story.coverImage.approved) {
            redirectPath = '/create/result';
          } else if (allPagesApproved && story.coverImage) {
            redirectPath = '/create/cover-studio';
          } else {
            redirectPath = '/create/image-studio';
          }
        } else {
          // Story generated but no images yet
          redirectPath = '/create/image-studio';
        }
      } else if (storyDescriptionLoaded) {
        // Story description exists but story not generated
        redirectPath = '/create/story-studio';
      } else if (story.images && story.images.length > 0) {
        // Images uploaded but no story description
        redirectPath = '/create/story-details';
      } else if (story.language && story.storyType) {
        // Language and story type selected but no images
        redirectPath = '/create/photos-upload';
      } else if (story.language) {
        // Language selected but no story type
        redirectPath = '/create/story-type';
      }

      console.log('✅ Session loaded successfully, redirecting to:', redirectPath);
      return { success: true, redirectPath };
    } catch (err: any) {
      console.error('❌ Failed to load session:', err);
      console.error('  Session ID attempted:', sessionIdToLoad);
      const errorMessage = err.message || 'Failed to load session';
      setError(errorMessage);
      setSubmissionOwnerEmail(null);
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  }, [apiCall, token, user?.isAdmin, user?.email]);

  // Image management functions
  const addImage = useCallback((image: ImageData) => {
    setImages(prev => prev.length < 10 ? [...prev, image] : prev);
  }, []);

  const removeImage = useCallback((index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateImageTags = useCallback((index: number, tags: string) => {
    setImages(prev => prev.map((img, i) => i === index ? { ...img, tags } : img));
  }, []);

  const tagsSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!token || !sessionId || images.length === 0 || !images.some((img) => img.uploaded)) return;
    if (tagsSaveTimerRef.current) clearTimeout(tagsSaveTimerRef.current);
    tagsSaveTimerRef.current = setTimeout(async () => {
      tagsSaveTimerRef.current = null;
      try {
        const tags = images.map((img) => img.tags || '');
        await apiCall(`/stories/${sessionId}/image-tags`, {
          method: 'PUT',
          body: JSON.stringify({ tags }),
        });
      } catch (err) {
        console.warn('Failed to persist image tags:', err);
      }
    }, 500);
    return () => {
      if (tagsSaveTimerRef.current) clearTimeout(tagsSaveTimerRef.current);
    };
  }, [token, sessionId, images, apiCall]);

  // Utility functions
  const clearError = useCallback(() => setError(null), []);

  const resetWizard = useCallback(() => {
    clearAdminWorkSessionStorage();
    setSessionId(null);
    setSubmissionOwnerEmail(null);
    setLanguage(null);
    setStoryTypeState(null);
    setStoryStyleState("watercolor");
    setImages([]);
    setStoryDescription("");
    setStoryGuidelines("");
    setStoryTitle("");
    setError(null);
    setIsLoading(false);
    useWizardStore.getState().resetStoryDetails();
  }, []);

  // Drop wizard + presigned URL cache when auth ends or a different account signs in (avoids stale sessionId / img cache pairing with a new JWT).
  useEffect(() => {
    const hadToken = prevAuthTokenRef.current;
    prevAuthTokenRef.current = token;

    if (hadToken && !token) {
      resetWizard();
      clearPresignCache();
      prevAuthUserIdRef.current = null;
      return;
    }

    const uid = user?.id;
    if (token && uid) {
      const prevUid = prevAuthUserIdRef.current;
      prevAuthUserIdRef.current = uid;
      if (prevUid && prevUid !== uid) {
        resetWizard();
        clearPresignCache();
      }
    }
  }, [token, user?.id, resetWizard]);

  return (
    <WizardContext.Provider value={{
      // Session
      sessionId,
      setSessionId,
      submissionOwnerEmail,
      
      // Language
      language,
      setLanguage,
      
      // Story Type
      storyType,
      setStoryType,
      storyStyle,
      setStoryStyle: setStoryStyleState,
      updateStoryType,
      
      // Images
      images,
      addImage,
      removeImage,
      updateImageTags,
      uploadImages,
      persistImageTags,
      
      // Story
      storyDescription,
      setStoryDescription,
      storyGuidelines,
      setStoryGuidelines,
      storyTitle,
      setStoryTitle,
      submitStoryDescription,
      
      // API functions
      createStorySession,
      generateStory,
      loadSession,
      
      // State
      isLoading,
      error,
      clearError,
      resetWizard
    }}>
      {children}
    </WizardContext.Provider>
  );
};

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) throw new Error("useWizard must be used within WizardProvider");
  return context;
};